import React, { useState, useEffect, useCallback, FormEvent } from 'react';
import { adminApi } from '../api/admin';
import { AccessCode, SiteStatus } from '../types';

type Tab = 'codes' | 'generate' | 'timer';

const ADMIN_SESSION_KEY = 'adminSession';
const SESSION_DURATION_MS = 60 * 60 * 1000; // 1 hour

// Helper to check if cached session is valid
function getCachedAdminSession(): string | null {
  const cached = localStorage.getItem(ADMIN_SESSION_KEY);
  if (!cached) return null;

  try {
    const { password, expiresAt } = JSON.parse(cached);
    if (Date.now() < expiresAt) {
      return password;
    }
    // Expired - clear it
    localStorage.removeItem(ADMIN_SESSION_KEY);
  } catch {
    localStorage.removeItem(ADMIN_SESSION_KEY);
  }
  return null;
}

// Helper to save admin session
function saveAdminSession(password: string): void {
  localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify({
    password,
    expiresAt: Date.now() + SESSION_DURATION_MS,
  }));
}

// Helper to clear admin session
function clearAdminSession(): void {
  localStorage.removeItem(ADMIN_SESSION_KEY);
}

export function AdminPage() {
  // Admin auth state - check for cached session on init
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(true); // Start true to check cache

  // Tab state
  const [activeTab, setActiveTab] = useState<Tab>('codes');

  // Codes state
  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [codesError, setCodesError] = useState('');

  // Generate state
  const [newEmail, setNewEmail] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');
  const [generateError, setGenerateError] = useState('');
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateSuccess, setGenerateSuccess] = useState('');

  // Generic code state
  const [genericCustomTitle, setGenericCustomTitle] = useState('');
  const [genericCollectionHandle, setGenericCollectionHandle] = useState('vip-products');

  // Timer state
  const [siteStatus, setSiteStatus] = useState<SiteStatus | null>(null);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [timerLoading, setTimerLoading] = useState(false);
  const [timerError, setTimerError] = useState('');

  // Load codes and config helper
  const loadCodesAndConfig = async (password: string) => {
    try {
      const [codesData, configData] = await Promise.all([
        adminApi.getCodes(password),
        adminApi.getConfig(password),
      ]);
      setCodes(codesData);
      setSiteStatus(configData.status);
    } catch (err: any) {
      console.error('Failed to load initial data:', err);
    }
  };

  // Check for cached admin session on mount
  useEffect(() => {
    const cachedPassword = getCachedAdminSession();
    if (cachedPassword) {
      // Verify the cached password is still valid
      adminApi.login(cachedPassword)
        .then(async (success) => {
          if (success) {
            setAdminPassword(cachedPassword);
            setIsAdminAuthenticated(true);
            // Load codes and config after restoring session
            await loadCodesAndConfig(cachedPassword);
          } else {
            clearAdminSession();
          }
        })
        .catch(() => {
          clearAdminSession();
        })
        .finally(() => {
          setLoginLoading(false);
        });
    } else {
      setLoginLoading(false);
    }
  }, []);

  // Admin login
  const handleAdminLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const success = await adminApi.login(adminPassword);
      if (success) {
        saveAdminSession(adminPassword);
        setIsAdminAuthenticated(true);
        // Load codes and config after login
        await loadCodesAndConfig(adminPassword);
      } else {
        setLoginError('Invalid password');
      }
    } catch (err: any) {
      setLoginError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    clearAdminSession();
    setIsAdminAuthenticated(false);
    setAdminPassword('');
  };

  // Load access codes
  const loadCodes = async () => {
    setCodesLoading(true);
    setCodesError('');

    try {
      const data = await adminApi.getCodes(adminPassword);
      setCodes(data);
    } catch (err: any) {
      setCodesError(err.message || 'Failed to load codes');
    } finally {
      setCodesLoading(false);
    }
  };

  // Load site config
  const loadConfig = useCallback(async () => {
    try {
      const data = await adminApi.getConfig(adminPassword);
      setSiteStatus(data.status);
    } catch (err: any) {
      setTimerError(err.message || 'Failed to load config');
    }
  }, [adminPassword]);

  // Generate single code
  const handleGenerateSingle = async (e: FormEvent) => {
    e.preventDefault();
    setGenerateError('');
    setGenerateSuccess('');
    setGenerateLoading(true);

    try {
      const code = await adminApi.createCode(adminPassword, newEmail.trim());
      setGenerateSuccess(`Created code ${code.code} for ${code.email}`);
      setNewEmail('');
      loadCodes();
    } catch (err: any) {
      setGenerateError(err.response?.data?.error || err.message || 'Failed to generate code');
    } finally {
      setGenerateLoading(false);
    }
  };

  // Generate bulk codes
  const handleGenerateBulk = async (e: FormEvent) => {
    e.preventDefault();
    setGenerateError('');
    setGenerateSuccess('');
    setGenerateLoading(true);

    const emails = bulkEmails
      .split('\n')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    if (emails.length === 0) {
      setGenerateError('Please enter at least one email');
      setGenerateLoading(false);
      return;
    }

    try {
      const result = await adminApi.createBulkCodes(adminPassword, emails);
      const successCount = result.success.length;
      const errorCount = result.errors.length;

      if (errorCount > 0) {
        setGenerateError(`${errorCount} failed: ${result.errors.map((e) => e.email).join(', ')}`);
      }
      if (successCount > 0) {
        setGenerateSuccess(`Created ${successCount} access codes`);
        setBulkEmails('');
        loadCodes();
      }
    } catch (err: any) {
      setGenerateError(err.response?.data?.error || err.message || 'Failed to generate codes');
    } finally {
      setGenerateLoading(false);
    }
  };

  // Generate generic code (no email required)
  const handleGenerateGeneric = async (e: FormEvent) => {
    e.preventDefault();
    setGenerateError('');
    setGenerateSuccess('');
    setGenerateLoading(true);

    try {
      const code = await adminApi.createCode(adminPassword, {
        customTitle: genericCustomTitle.trim() || undefined,
        collectionHandle: genericCollectionHandle.trim() || 'vip-products',
      });
      const titleInfo = code.custom_title ? ` with title "${code.custom_title}"` : '';
      const collectionInfo = ` (collection: ${code.collection_handle})`;
      setGenerateSuccess(`Created generic code ${code.code}${titleInfo}${collectionInfo}`);
      setGenericCustomTitle('');
      loadCodes();
    } catch (err: any) {
      setGenerateError(err.response?.data?.error || err.message || 'Failed to generate code');
    } finally {
      setGenerateLoading(false);
    }
  };

  // Delete code
  const handleDeleteCode = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this access code?')) {
      return;
    }

    try {
      await adminApi.deleteCode(adminPassword, id);
      loadCodes();
    } catch (err: any) {
      setCodesError(err.message || 'Failed to delete code');
    }
  };

  // Toggle code active
  const handleToggleCode = async (id: number) => {
    try {
      await adminApi.toggleCode(adminPassword, id);
      loadCodes();
    } catch (err: any) {
      setCodesError(err.message || 'Failed to toggle code');
    }
  };

  // Start timer
  const handleStartTimer = async () => {
    setTimerError('');
    setTimerLoading(true);

    try {
      await adminApi.startCountdown(adminPassword, durationMinutes);
      loadConfig();
    } catch (err: any) {
      setTimerError(err.message || 'Failed to start timer');
    } finally {
      setTimerLoading(false);
    }
  };

  // Stop timer
  const handleStopTimer = async () => {
    if (!window.confirm('Are you sure you want to stop the countdown? This will deactivate the site.')) {
      return;
    }

    setTimerError('');
    setTimerLoading(true);

    try {
      await adminApi.stopCountdown(adminPassword, true);
      loadConfig();
    } catch (err: any) {
      setTimerError(err.message || 'Failed to stop timer');
    } finally {
      setTimerLoading(false);
    }
  };

  // Copy code to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Format time remaining
  const formatTimeRemaining = (seconds: number | null): string => {
    if (seconds === null || seconds <= 0) return 'Expired';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'Hour' : 'Hours'}`);
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'Minute' : 'Minutes'}`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs} ${secs === 1 ? 'Second' : 'Seconds'}`);

    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
    return parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
  };

  // Refresh timer status periodically
  useEffect(() => {
    if (!isAdminAuthenticated) return;

    const interval = setInterval(loadConfig, 10000);
    return () => clearInterval(interval);
  }, [isAdminAuthenticated, loadConfig]);

  // Admin login form
  if (!isAdminAuthenticated) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <h1>Admin Login</h1>
          <form onSubmit={handleAdminLogin}>
            <div className="form-group">
              <label htmlFor="adminPassword">Admin Password</label>
              <input
                type="password"
                id="adminPassword"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                disabled={loginLoading}
              />
            </div>
            {loginError && <div className="error-message">{loginError}</div>}
            <button type="submit" disabled={loginLoading}>
              {loginLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <h1>Admin Dashboard</h1>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </header>

      <nav className="admin-tabs">
        <button
          className={activeTab === 'codes' ? 'active' : ''}
          onClick={() => setActiveTab('codes')}
        >
          Access Codes
        </button>
        <button
          className={activeTab === 'generate' ? 'active' : ''}
          onClick={() => setActiveTab('generate')}
        >
          Generate Codes
        </button>
        <button
          className={activeTab === 'timer' ? 'active' : ''}
          onClick={() => setActiveTab('timer')}
        >
          Timer Control
        </button>
      </nav>

      <main className="admin-content">
        {/* Codes Tab */}
        {activeTab === 'codes' && (
          <div className="codes-tab">
            <div className="tab-header">
              <h2>Access Codes ({codes.length})</h2>
              <button onClick={loadCodes} disabled={codesLoading}>
                {codesLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {codesError && <div className="error-message">{codesError}</div>}

            <table className="codes-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Code</th>
                  <th>Custom Title</th>
                  <th>Collection</th>
                  <th>Uses</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((code) => (
                  <tr key={code.id} className={code.is_active ? '' : 'inactive'}>
                    <td>{code.email || <em>Generic</em>}</td>
                    <td>
                      <code>{code.code}</code>
                      <button
                        className="copy-btn"
                        onClick={() => copyToClipboard(code.code)}
                        title="Copy code"
                      >
                        Copy
                      </button>
                    </td>
                    <td>{code.custom_title || '-'}</td>
                    <td>{code.collection_handle || 'vip-products'}</td>
                    <td>{code.use_count}</td>
                    <td>{code.is_active ? 'Active' : 'Inactive'}</td>
                    <td>
                      <button onClick={() => handleToggleCode(code.id)}>
                        {code.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button onClick={() => handleDeleteCode(code.id)} className="delete-btn">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Generate Tab */}
        {activeTab === 'generate' && (
          <div className="generate-tab">
            <h2>Generate Access Codes</h2>

            {generateError && <div className="error-message">{generateError}</div>}
            {generateSuccess && <div className="success-message">{generateSuccess}</div>}

            <div className="generate-section">
              <h3>Single Code</h3>
              <form onSubmit={handleGenerateSingle}>
                <div className="form-group">
                  <label htmlFor="singleEmail">Email Address</label>
                  <input
                    type="email"
                    id="singleEmail"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="user@example.com"
                    disabled={generateLoading}
                  />
                </div>
                <button type="submit" disabled={generateLoading || !newEmail.trim()}>
                  Generate Code
                </button>
              </form>
            </div>

            <div className="generate-section">
              <h3>Bulk Generate</h3>
              <form onSubmit={handleGenerateBulk}>
                <div className="form-group">
                  <label htmlFor="bulkEmails">Email Addresses (one per line)</label>
                  <textarea
                    id="bulkEmails"
                    value={bulkEmails}
                    onChange={(e) => setBulkEmails(e.target.value)}
                    placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                    rows={10}
                    disabled={generateLoading}
                  />
                </div>
                <button type="submit" disabled={generateLoading || !bulkEmails.trim()}>
                  Generate All Codes
                </button>
              </form>
            </div>

            <div className="generate-section">
              <h3>Generic Code (No Email Required)</h3>
              <p className="section-description">
                Create a code that works with any email. Great for sharing with groups.
              </p>
              <form onSubmit={handleGenerateGeneric}>
                <div className="form-group">
                  <label htmlFor="genericCustomTitle">Custom Page Title (optional)</label>
                  <input
                    type="text"
                    id="genericCustomTitle"
                    value={genericCustomTitle}
                    onChange={(e) => setGenericCustomTitle(e.target.value)}
                    placeholder="e.g., Acme Corp VIP Access"
                    disabled={generateLoading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="genericCollectionHandle">Shopify Collection Handle</label>
                  <input
                    type="text"
                    id="genericCollectionHandle"
                    value={genericCollectionHandle}
                    onChange={(e) => setGenericCollectionHandle(e.target.value)}
                    placeholder="vip-products"
                    disabled={generateLoading}
                  />
                </div>
                <button type="submit" disabled={generateLoading}>
                  Generate Generic Code
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Timer Tab */}
        {activeTab === 'timer' && (
          <div className="timer-tab">
            <h2>Timer Control</h2>

            {timerError && <div className="error-message">{timerError}</div>}

            <div className="timer-status">
              <h3>Current Status</h3>
              <p>
                <strong>Site Active:</strong>{' '}
                {siteStatus?.isSiteActive ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Time Remaining:</strong>{' '}
                {formatTimeRemaining(siteStatus?.timeRemaining ?? null)}
              </p>
              {siteStatus?.countdownEndTime && (
                <p>
                  <strong>Ends At:</strong>{' '}
                  {new Date(siteStatus.countdownEndTime).toLocaleString()}
                </p>
              )}
            </div>

            <div className="timer-controls">
              <h3>Start New Countdown</h3>
              <div className="form-group">
                <label htmlFor="duration">Duration (minutes)</label>
                <input
                  type="number"
                  id="duration"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 0)}
                  min={1}
                  disabled={timerLoading}
                />
              </div>
              <div className="duration-presets">
                <button onClick={() => setDurationMinutes(30)}>30 min</button>
                <button onClick={() => setDurationMinutes(60)}>1 hour</button>
                <button onClick={() => setDurationMinutes(120)}>2 hours</button>
                <button onClick={() => setDurationMinutes(1440)}>24 hours</button>
                <button onClick={() => setDurationMinutes(10080)}>7 days</button>
              </div>
              <div className="timer-actions">
                <button
                  onClick={handleStartTimer}
                  disabled={timerLoading || durationMinutes <= 0}
                  className="start-btn"
                >
                  {timerLoading ? 'Starting...' : 'Start Countdown'}
                </button>
                <button
                  onClick={handleStopTimer}
                  disabled={timerLoading || !siteStatus?.isSiteActive}
                  className="stop-btn"
                >
                  Stop & Deactivate
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminPage;
